import { ChangeEvent, useState } from "react"
import {
  Button,
  Dialog,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot } from "@fortawesome/free-solid-svg-icons"

import { colors } from "../../../utils/theme"
import useSafetyPlanOptionStyles from "./options/useSafetyPlanOptionStyles"

interface AddPlaceDialogProps {
  open: boolean
  onClose: () => void
  onSave: (place: string) => void
}

/**
 * Modal dialog that lets the user type and save the name of a safe place
 * for a safety plan section. Manages its own text field state internally;
 * the parent only controls open/close visibility and receives the saved value.
 *
 * @param open    - Whether the dialog is visible.
 * @param onClose - Callback to close the dialog without saving.
 * @param onSave  - Callback invoked with the entered place name on save.
 */
export default function AddPlaceDialog({
  open,
  onClose,
  onSave,
}: AddPlaceDialogProps) {
  const [place, setPlace] = useState("")

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPlace(event.target.value)
  }

  const handleSave = () => {
    if (!place.trim()) return

    onSave(place.trim())
    setPlace("")
  }

  const handleClose = () => {
    setPlace("")
    onClose()
  }

  const sharedClasses = useSafetyPlanOptionStyles()
  const localClasses = useStyles()
  const classes = { ...sharedClasses, ...localClasses }
  return (
    <Dialog open={open} onClose={handleClose}>
      <div className={classes.modalContainer}>
        <div className={classes.modalBody}>
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: colors.primary.shadow,
              }}
            >
              <FontAwesomeIcon icon={faLocationDot} />
            </div>
            <Typography variant="h2" className={classes.addPlaceText}>
              Add a Place
            </Typography>
          </div>
          <div id="placeInfo" className={classes.placeInfoContainer}>
            <div className={classes.modalField}>
              <TextField
                id="add-place-name"
                label="Name of Place"
                value={place}
                onChange={handleChange}
                variant="outlined"
                placeholder=""
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <div className={classes.modalFooter}>
            <Button
              onClick={handleClose}
              className={classes.cancelModalBtn}
              disableElevation
            >
              <Typography
                className={classes.modalBtnTxt}
                style={{ color: colors.primary.shadow }}
              >
                Cancel
              </Typography>
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              className={classes.saveModalBtn}
              disabled={!place.trim()}
              disableElevation
            >
              <Typography
                className={classes.modalBtnTxt}
                style={{ color: colors.neutral.white }}
              >
                Save
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

const useStyles = makeStyles(() => ({
  placeInfoContainer: {
    display: "flex",
    padding: "24px 16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "24px",
    alignSelf: "stretch",
    borderRadius: "8px",
    border: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.safetyPlan.cardSecondaryBackground,
  },
  addPlaceText: {
    color: colors.safetyPlan.ink,
    lineHeight: "40px",
  },
}))
